import React, { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { CheckCircle2, CircleDot, Loader2, XCircle } from "lucide-react";
import axios from "../../../../axios";
import { toast } from "react-toastify";

const pickFirst = (...values) =>
  values.find((value) => value !== undefined && value !== null && value !== "");

const getPayload = (response) => {
  const data = response?.data;
  const result = data?.result || data?.data;

  if (result?.attempt && typeof result.attempt === "object") {
    return result.attempt;
  }

  return result || data || {};
};

const getRows = (payload) => {
  const candidates = [
    payload?.answerKey,
    payload?.answers,
    payload?.questions,
    payload?.responses,
    payload?.result?.answerKey,
    payload?.result?.answers,
    payload?.result?.questions,
    payload?.data?.answerKey,
    payload?.data?.answers,
    payload?.data?.questions,
    Array.isArray(payload) ? payload : null,
  ];

  return candidates.find((item) => Array.isArray(item)) || [];
};

const normalizeText = (value) =>
  value === undefined || value === null ? "" : String(value).trim();

const getQuestionSource = (item) => item?.question || item?.questionData || item;

const resolveAnswerKeyRow = (item, index) => {
  const source = getQuestionSource(item);

  return {
    id: item?._id || source?._id || index,
    number: item?.questionNo || item?.number || index + 1,
    question: normalizeText(
      pickFirst(
        item?.questionText,
        source?.question,
        source?.questionEng,
        source?.englishQuestion,
        `Question ${index + 1}`,
      ),
    ),
    correctAnswer: normalizeText(
      pickFirst(
        item?.correctAnswer,
        item?.rightAnswer,
        item?.rightAnsEng,
        source?.answer,
        source?.correctAnswer,
        "",
      ),
    ),
    userAnswer: normalizeText(
      pickFirst(item?.userAnswer, item?.selectedAnswer, item?.submittedAnswer, item?.answer, ""),
    ),
  };
};

const MyTestResult = () => {
  const { mockTestId } = useParams();
  const [payload, setPayload] = useState(null);
  const [answerKey, setAnswerKey] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchResult = async () => {
      setLoading(true);

      try {
        const resultResponse = await axios.get(
          `/institute/mock-tests/${mockTestId}/my-result`,
        );

        setPayload(getPayload(resultResponse));
      } catch (error) {
        toast.error("Unable to load my test result");
      } finally {
        setLoading(false);
      }

      try {
        const answerKeyResponse = await axios.get(
          `/institute/mock-tests/${mockTestId}/answer-key`,
        );
        setAnswerKey(getPayload(answerKeyResponse));
      } catch {
        setAnswerKey(null);
      }
    };

    fetchResult();
  }, [mockTestId]);

  const result = useMemo(() => {
    const total = Number(
      pickFirst(
        payload?.summary?.totalQuestions,
        payload?.totalQuestions,
        payload?.mockTest?.totalQuestions,
        0,
      ),
    );
    const correct = Number(
      pickFirst(payload?.summary?.correct, payload?.correct, payload?.rightCount, 0),
    );
    const wrong = Number(
      pickFirst(payload?.summary?.wrong, payload?.wrong, payload?.wrongCount, 0),
    );
    const skipped = Number(
      pickFirst(payload?.summary?.skipped, payload?.skipped, payload?.skipCount, 0),
    );

    return {
      title: pickFirst(payload?.mockTest?.name, payload?.mockTest?.title, "My Test Report"),
      score: Number(pickFirst(payload?.score, 0)),
      total,
      correct,
      wrong,
      skipped,
      accuracy:
        total > 0
          ? Math.round((correct / total) * 100)
          : Number(pickFirst(payload?.percentage, 0)),
      timeTaken: pickFirst(payload?.summary?.timeTakenSeconds, payload?.timeTakenSeconds, 0),
    };
  }, [payload]);

  const answerKeyRows = useMemo(
    () => getRows(answerKey || payload || {}).map(resolveAnswerKeyRow),
    [answerKey, payload],
  );

  return (
    <div className="min-h-screen bg-[#fbfafc] pb-24 text-[#211629]">
      <main className="mx-auto max-w-5xl px-4 pt-8 pb-5 sm:px-6 lg:px-8 lg:pt-14">
        {loading ? (
          <div className="flex min-h-[300px] items-center justify-center rounded-3xl bg-white shadow-sm">
            <Loader2 className="h-8 w-8 animate-spin text-[#562566]" />
          </div>
        ) : (
          <>
            <section className="rounded-[1.5rem] bg-[#562566] p-5 text-left text-white shadow-[0_14px_28px_rgba(86,37,102,0.16)] lg:p-7">
              <p className="text-xs font-bold text-white/75">My Test Report</p>
              <h1 className="mt-1 text-2xl font-extrabold leading-tight">
                {result.title}
              </h1>

              <div className="mt-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <div className="rounded-xl bg-white px-4 py-3 text-[#211629]">
                  <p className="text-xs font-bold text-[#8a8490]">Score</p>
                  <p className="mt-1 text-lg font-extrabold text-[#562566]">
                    {result.score}/{result.total}
                  </p>
                </div>
                <div className="rounded-xl bg-white px-4 py-3 text-[#211629]">
                  <p className="text-xs font-bold text-[#8a8490]">Accuracy</p>
                  <p className="mt-1 text-lg font-extrabold text-green-600">
                    {result.accuracy}%
                  </p>
                </div>
                <div className="rounded-xl bg-white px-4 py-3 text-[#211629]">
                  <p className="text-xs font-bold text-[#8a8490]">Skipped</p>
                  <p className="mt-1 text-lg font-extrabold text-[#F5A623]">
                    {result.skipped}
                  </p>
                </div>
                <div className="rounded-xl bg-white px-4 py-3 text-[#211629]">
                  <p className="text-xs font-bold text-[#8a8490]">Time</p>
                  <p className="mt-1 text-lg font-extrabold text-[#562566]">
                    {result.timeTaken}s
                  </p>
                </div>
              </div>
            </section>

            <section className="mt-5 grid gap-4 sm:grid-cols-3">
              <div className="rounded-2xl bg-white p-5 shadow-sm">
                <CheckCircle2 className="h-6 w-6 text-green-600" />
                <p className="mt-2 text-sm font-bold text-gray-500">Correct</p>
                <p className="text-2xl font-extrabold text-green-600">{result.correct}</p>
              </div>
              <div className="rounded-2xl bg-white p-5 shadow-sm">
                <XCircle className="h-6 w-6 text-red-500" />
                <p className="mt-2 text-sm font-bold text-gray-500">Wrong</p>
                <p className="text-2xl font-extrabold text-red-500">{result.wrong}</p>
              </div>
              <div className="rounded-2xl bg-white p-5 shadow-sm">
                <CircleDot className="h-6 w-6 text-[#F5A623]" />
                <p className="mt-2 text-sm font-bold text-gray-500">Skipped</p>
                <p className="text-2xl font-extrabold text-[#F5A623]">{result.skipped}</p>
              </div>
            </section>

            <section className="mt-5 rounded-2xl border border-[#eee8f0] bg-white p-4 text-left shadow-sm lg:p-6">
              <h2 className="text-base font-extrabold text-[#211629]">
                Answer Key
              </h2>
              {answerKeyRows.length ? (
                <div className="mt-4 grid gap-4 lg:grid-cols-2">
                  {answerKeyRows.map((row) => (
                    <div
                      key={row.id}
                      className="rounded-2xl border border-[#eee8f0] bg-[#fbfafc] p-4"
                    >
                      <p className="text-xs font-extrabold text-[#562566]">
                        Q{row.number}
                      </p>
                      <h3 className="mt-2 text-sm font-semibold leading-6 text-[#211629]">
                        {row.question}
                      </h3>
                      <div className="mt-3 grid gap-2 text-sm font-semibold">
                        <p className="rounded-xl bg-[#E7F8EE] px-3 py-2 text-[#15935E]">
                          Correct: {row.correctAnswer || "-"}
                        </p>
                        <p className="rounded-xl bg-[#F4ECFF] px-3 py-2 text-[#6B2B86]">
                          Your Answer: {row.userAnswer || "Skipped"}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="mt-4 rounded-xl bg-[#f7f2f8] p-4 text-sm font-semibold text-[#6e6875]">
                  Answer key is not available yet.
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
};

export default MyTestResult;